import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function startGoogleLogin() {
  const redirectUrl = window.location.origin + "/auth/callback";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Welcome back.");
      navigate(params.get("next") || "/library");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid credentials");
    } finally { setBusy(false); }
  };

  return (
    <Layout hideFooter>
      <SEO title="Sign in" description="Sign in to your South on Leadership account." noindex />
      <section className="container-narrow py-16 md:py-24 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-6 hidden lg:block">
          <span className="overline text-brand-rust">Sign in</span>
          <h1 className="font-display text-6xl mt-4 text-brand-ink leading-[1.05]">
            Welcome back to <em className="italic">the library</em>.
          </h1>
          <p className="mt-6 text-brand-inkMuted max-w-md">
            Pick up where you left off — your books, your courses, your progress.
          </p>
        </div>
        <div className="lg:col-span-6 max-w-md w-full lg:ml-auto">
          <div className="bg-white border border-brand-line p-8 md:p-10">
            <h2 className="font-display text-3xl text-brand-ink">Sign in</h2>
            <p className="text-sm text-brand-inkMuted mt-2">Or <Link to="/register" className="text-brand-rust hover:underline" data-testid="link-to-register">create an account</Link></p>

            <button
              data-testid="google-login-btn"
              onClick={startGoogleLogin}
              className="mt-6 w-full border border-brand-ink hover:bg-brand-ink hover:text-white py-3 text-sm font-medium tracking-wide transition-colors flex items-center justify-center gap-3"
            >
              <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden>
                <path d="M17.64 9.205c0-.638-.057-1.252-.164-1.841H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
                <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.583-5.036-3.71H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/>
                <path d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
                <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
              </svg>
              Continue with Google
            </button>

            <div className="my-6 flex items-center gap-3 overline text-brand-inkMuted">
              <div className="flex-1 h-px bg-brand-line" /> or <div className="flex-1 h-px bg-brand-line" />
            </div>

            <form onSubmit={submit} className="space-y-4" data-testid="login-form">
              <div>
                <Label htmlFor="email" className="overline text-brand-inkMuted">Email</Label>
                <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" className="rounded-none mt-2 border-brand-line focus:border-brand-ink focus:ring-0" />
              </div>
              <div>
                <Label htmlFor="password" className="overline text-brand-inkMuted">Password</Label>
                <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password" className="rounded-none mt-2 border-brand-line focus:border-brand-ink focus:ring-0" />
              </div>
              <button
                type="submit"
                disabled={busy}
                data-testid="login-submit"
                className="w-full bg-brand-ink text-white hover:bg-black py-3 text-sm font-medium tracking-wide transition-colors disabled:opacity-60"
              >{busy ? "Signing in…" : "Sign in"}</button>
              <p className="text-center text-xs text-brand-inkMuted pt-2">
                <Link
                  to="/forgot-password"
                  data-testid="login-forgot-password"
                  className="text-brand-rust hover:text-brand-ink underline underline-offset-4"
                >Forgot your password?</Link>
              </p>
            </form>
          </div>
        </div>
      </section>
    </Layout>
  );
}
